import { AuthProvider } from '@/shared/context/auth-context/AuthContext';
import { LOGIN_ROUTE_INDICATOR, getAuthData, getAuthUserFromResponse } from '@/shared/utils/auth';
import { FunctionComponent, ReactElement, ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedUser } from '@/api/auth';
import { AuthUser, Roles } from '@/core/types';
import { pushToDataLayer } from '@/shared/utils/analytics';
import { LocationState } from './types';

interface AuthGuardProps {
	element: ReactNode;
}

const AuthGuard: FunctionComponent<AuthGuardProps> = ({ element }): ReactElement => {
	const accessToken = getAuthData()?.accessToken;
	const location = useLocation() as { state?: LocationState };

	const trackUserData = (authUser: AuthUser) => {
		pushToDataLayer({
			event: location.state?.from === LOGIN_ROUTE_INDICATOR ? 'login' : 'user_param_push',
			user_id: authUser.id,
			user_type: authUser.role,
			user_category: authUser?.role === Roles.LEVIANT_ADMIN ? 'Admin' : 'Client',
			user_customer_name: authUser.customer?.name,
		});
	};

	const { data } = useQuery({
		queryKey: ['me', accessToken],
		queryFn: () => getAuthenticatedUser(),
		select: (response) => getAuthUserFromResponse(response),
		enabled: !!accessToken,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (data) {
			trackUserData(data);
			/* Need to clear location state after login, because when page is refreshed right after login location state
			is preserved so wrong event is sent to analytics - login instead of user_param_push */
			if (location.state?.from === LOGIN_ROUTE_INDICATOR) {
				window.history.replaceState(null, '');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	if (!accessToken) {
		return <Navigate to="/auth/login" replace state={{ from: location }} />;
	}

	return <AuthProvider>{element}</AuthProvider>;
};

export default AuthGuard;
